import React, { useState, useEffect } from 'react';
import { Settings, Shield, Mail, Database, Bell, Globe, Key, CheckCircle2, XCircle, Loader, Bot, Save } from 'lucide-react';
import api from '../../services/api';

const PlatformSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [apiKeys, setApiKeys] = useState({
    openai_api_key: '',
    openai_model: 'gpt-4-vision-preview'
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'ai-keys', label: 'API', icon: Key },
    { id: 'ai-risk-manager', label: 'AI Risk Manager', icon: Bot },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'integrations', label: 'Integrations', icon: Globe },
  ];

  useEffect(() => {
    if (activeTab === 'ai-keys') {
      fetchApiKeys();
    }
    if (activeTab === 'ai-risk-manager') {
      fetchAiPrompt();
    }
  }, [activeTab]);

  const fetchAiPrompt = async () => {
    try {
      setPromptLoading(true);
      const response = await api.get('/kyb-levels/ai-risk-manager/prompt');
      setAiPrompt(response.data.prompt || '');
    } catch (error) {
      console.error('Failed to fetch AI prompt:', error);
    } finally {
      setPromptLoading(false);
    }
  };

  const handleSaveAiPrompt = async () => {
    try {
      setPromptSaving(true);
      await api.put('/kyb-levels/ai-risk-manager/prompt', { prompt: aiPrompt });
      setPromptSaved(true);
      setTimeout(() => setPromptSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save AI prompt:', error);
      alert('Failed to save AI prompt');
    } finally {
      setPromptSaving(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await api.get('/platform-settings/api-keys');
      if (response.data.settings) {
        setApiKeys(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    }
  };

  const handleSaveApiKeys = async () => {
    try {
      setSaving(true);
      await api.post('/platform-settings/api-keys', apiKeys);
      alert('API keys saved successfully');
    } catch (error) {
      console.error('Failed to save API keys:', error);
      alert('Failed to save API keys');
    } finally {
      setSaving(false);
    }
  };

  const handleTestOpenAI = async () => {
    if (!apiKeys.openai_api_key) {
      alert('Please enter an OpenAI API key first');
      return;
    }

    try {
      setTestingConnection(true);
      setConnectionStatus(null);
      const response = await api.post('/platform-settings/test-openai', {
        api_key: apiKeys.openai_api_key,
        model: apiKeys.openai_model
      });
      setConnectionStatus({ success: true, message: response.data.message });
    } catch (error) {
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || 'Connection failed'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Platform Settings</h1>
        <p className="text-gray-600 mt-1">Configure platform-wide settings and preferences</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platform Name
                    </label>
                    <input
                      type="text"
                      defaultValue="Acquiva"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Support Email
                    </label>
                    <input
                      type="email"
                      defaultValue="support@acquiva.co"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option>UTC</option>
                      <option>America/New_York</option>
                      <option>Europe/London</option>
                      <option>Asia/Tokyo</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai-keys' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">AI & API Keys Configuration</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Configure API keys for AI-powered verification features like household bill scanning and document analysis.
                </p>

                {/* OpenAI Configuration */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <Key className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">OpenAI API Configuration</h4>
                      <p className="text-sm text-gray-600">Required for AI-powered document verification (Household Bills, etc.)</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OpenAI API Key *
                      </label>
                      <input
                        type="password"
                        value={apiKeys.openai_api_key}
                        onChange={(e) => setApiKeys({ ...apiKeys, openai_api_key: e.target.value })}
                        placeholder="sk-..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">OpenAI Platform</a>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OpenAI Model
                      </label>
                      <select
                        value={apiKeys.openai_model}
                        onChange={(e) => setApiKeys({ ...apiKeys, openai_model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="gpt-4-vision-preview">GPT-4 Vision (Recommended for document scanning)</option>
                        <option value="gpt-4-turbo">GPT-4 Turbo</option>
                        <option value="gpt-4">GPT-4</option>
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Budget option)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        GPT-4 Vision is required for image-based document verification
                      </p>
                    </div>

                    {/* Test Connection */}
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={handleTestOpenAI}
                        disabled={testingConnection || !apiKeys.openai_api_key}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                      >
                        {testingConnection ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          'Test Connection'
                        )}
                      </button>

                      {connectionStatus && (
                        <div className={`flex items-center ${connectionStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                          {connectionStatus.success ? (
                            <CheckCircle2 className="w-5 h-5 mr-2" />
                          ) : (
                            <XCircle className="w-5 h-5 mr-2" />
                          )}
                          <span className="text-sm font-medium">{connectionStatus.message}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveApiKeys}
                    disabled={saving}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save API Keys'}
                  </button>
                </div>

                {/* Usage Information */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h5 className="font-semibold text-blue-900 mb-2">Features Using OpenAI API:</h5>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Household Bill Verification - Extracts name, address, company, and bill type from scanned bills</li>
                    <li>Document Analysis - AI-powered document authenticity checks</li>
                    <li>Future AI Features - Additional verification capabilities as they're added</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai-risk-manager' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">AI Risk Manager Configuration</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Configure the AI prompt used when analyzing KYB applications that require manual review.
                  The AI will use this prompt to analyze applications and ask follow-up questions.
                </p>

                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Bot className="w-6 h-6 text-purple-600 mr-3" />
                      <div>
                        <h4 className="font-semibold text-gray-900">AI System Prompt</h4>
                        <p className="text-sm text-gray-600">Defines how the AI analyzes KYB applications</p>
                      </div>
                    </div>
                    {promptSaved && (
                      <span className="flex items-center text-green-600 text-sm">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Saved
                      </span>
                    )}
                  </div>

                  {promptLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        rows={16}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                        placeholder="Enter the AI system prompt..."
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        The AI will analyze applications and respond in JSON format with analysis, questions, and recommendations.
                        Maximum 5 rounds of Q&A are allowed.
                      </p>

                      <div className="flex justify-end mt-4">
                        <button
                          onClick={handleSaveAiPrompt}
                          disabled={promptSaving}
                          className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300"
                        >
                          {promptSaving ? (
                            <><Loader className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save Prompt</>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h5 className="font-semibold text-purple-900 mb-2">How AI Risk Manager Works:</h5>
                  <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
                    <li>Triggered when a KYB application requires manual review (PENDING_REVIEW status)</li>
                    <li>Analyzes sanitized application data (names redacted, locations kept)</li>
                    <li>Asks targeted questions about concerning areas (max 5 rounds)</li>
                    <li>Can recommend approval, rejection, or additional validation workflows</li>
                    <li>All conversations are recorded in the database for audit</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Require 2FA for all users</p>
                      <p className="text-sm text-gray-500">Force all users to enable two-factor authentication</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Session timeout</p>
                      <p className="text-sm text-gray-500">Automatically log out inactive users</p>
                    </div>
                    <select className="px-3 py-2 border border-gray-300 rounded-lg">
                      <option>30 minutes</option>
                      <option>1 hour</option>
                      <option>4 hours</option>
                      <option>24 hours</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Email Configuration</h3>
                <p className="text-sm text-gray-600 mb-4">Configure SMTP settings for platform emails</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                    <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                    <input type="number" defaultValue="587" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;

